import {
  TAB_QUERY_KEY,
  resolveTabIn,
  searchWithTabFor
} from '../../util_components/page_tabs/page_tabs';
import type { TabDef as GenericTabDef } from '../../util_components/page_tabs/page_tabs';
import { ADVISOR_URL_PARAMS } from './advisors/advisors';
import { REP_PARAM } from './my_leads/my_leads';
import { is_manager as isManager, level_from_me as levelFromMe } from './level';
import type { SalesMe } from '../sales_team/api';

export type PageTab =
  | 'tasks'
  | 'my_leads'
  | 'team_leads'
  | 'session_allocation'
  | 'generation'
  | 'report'
  | 'sales_users'
  | 'advisors';

// My Leads (own page) and Team's Leads view (manager picks a rep); both omitted by default.
export interface LeadsAccess {
  mine?: boolean;
  team?: boolean;
}

export type TabDef = GenericTabDef<PageTab>;

export const DEFAULT_TAB: PageTab = 'tasks';

const MANAGER_TABS: PageTab[] = ['sales_users', 'advisors'];

const ALL_TABS: TabDef[] = [
  { id: 'tasks', label: 'Tasks' },
  { id: 'my_leads', label: 'My Leads' },
  { id: 'team_leads', label: "Team's Leads view" },
  { id: 'session_allocation', label: 'Session allocation' },
  { id: 'generation', label: 'Task generation' },
  { id: 'report', label: 'Instant task report' },
  { id: 'sales_users', label: 'Sales users' },
  { id: 'advisors', label: 'Advisors' }
];

// The report tab is SUPER_USER-only; the sales-users and advisors tabs are for
// SUPER_USER or sales team managers. They are absent (not disabled) otherwise.
export const tabList = (
  canViewReport: boolean,
  canManageSalesUsers = false,
  canAllocateSessions = false,
  leads: LeadsAccess = {}
): TabDef[] =>
  ALL_TABS.filter(
    (t) =>
      (t.id !== 'report' || canViewReport) &&
      (!MANAGER_TABS.includes(t.id) || canManageSalesUsers) &&
      (t.id !== 'session_allocation' || canAllocateSessions) &&
      (t.id !== 'my_leads' || !!leads.mine) &&
      (t.id !== 'team_leads' || !!leads.team)
  );

// Mirrors the server gate (SUPER_USER, MANAGER, active L2/L3); /me only reports active levels.
export const canAllocateSessions = (
  me: SalesMe | null | undefined,
  isSuperUser: boolean
): boolean => isSuperUser || !!me?.can_assign || ['MANAGER', 'L2', 'L3'].includes(me?.level || '');

// Active L1-L3 reps see their own page; other SALES users (no roster row, LEFT, no level)
// get the tab with an "ask your manager" message. Managers and super users use Team's Leads.
export const canSeeMyLeads = (
  me: SalesMe | null | undefined,
  isSuperUser: boolean,
  isSales: boolean
): boolean => {
  if (levelFromMe(me)) return true;
  if (!me || isManager(me) || isSuperUser) return false;
  return isSales;
};

// Mirrors the server gate for ?ops_uuid= (isManager(): active MANAGER or SUPER_USER).
export const canSeeTeamLeads = (me: SalesMe | null | undefined, isSuperUser: boolean): boolean =>
  isSuperUser || isManager(me);

export const resolveTab = (
  search: string | URLSearchParams,
  canViewReport: boolean,
  canManageSalesUsers = false,
  canAllocate = false,
  leads: LeadsAccess = {}
): PageTab =>
  resolveTabIn(
    tabList(canViewReport, canManageSalesUsers, canAllocate, leads),
    DEFAULT_TAB,
    search
  );

// A ?tab=my_leads/team_leads link waits for /me (which grants the tab) instead of
// rendering Tasks first and jumping.
export const leadsTabPending = (
  search: string | URLSearchParams,
  active: PageTab,
  meLoaded: boolean
): boolean => {
  if (meLoaded) return false;
  const params = new URLSearchParams(typeof search === 'string' ? search : search.toString());
  const want = params.get(TAB_QUERY_KEY);
  return (want === 'my_leads' || want === 'team_leads') && want !== active;
};

// The advisor/view/from/to deep-link params only mean something on the advisors tab,
// ?rep= only on Team's Leads view.
export const searchWithTab = (search: string | URLSearchParams, tab: PageTab): string => {
  const params = new URLSearchParams(typeof search === 'string' ? search : search.toString());
  if (tab !== 'advisors') ADVISOR_URL_PARAMS.forEach((k) => params.delete(k));
  if (tab !== 'team_leads') params.delete(REP_PARAM);
  return searchWithTabFor(DEFAULT_TAB, params, tab);
};
